import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, RefreshCcw, Plus, HandMetal, Mail } from "lucide-react";
import { Button } from "components/ui/button";
import AddIdentity from "components/identity/add-identity/AddIdentity";

interface ExecuteButtonNodeProps {
  node: any;
  identities: any[];
  isOpen: boolean;
  isRefreshing?: boolean;
  onToggleAccordion: () => void;
  onAddNode: (nodeData: any) => void;
  onAddIdentity: (identity: any) => void;
  onRefreshIdentities: () => void;
  onDragStart?: (e: React.DragEvent, nodeData: any) => void;
}

const ExecuteButtonNode: React.FC<ExecuteButtonNodeProps> = ({
  node, identities, isOpen, isRefreshing = false,
  onToggleAccordion, onAddNode, onAddIdentity: _onAddIdentity, onRefreshIdentities, onDragStart,
}) => {
  const [showModal, setShowModal] = useState(false);

  const filteredIdentities = useMemo(
    () => identities.filter(i => i.status === "Success"),
    [identities]
  );

  const addNode = (identity: any) => {
    onAddNode({ name: identity.email, type: "identity" });
  };

  const dragStart = (e: React.DragEvent, identity: any) => {
    const nodeData = { name: identity.email, type: "identity", friendlyName: identity.friendlyName };
    e.dataTransfer.setData("application/json", JSON.stringify(nodeData));
    onDragStart?.(e, nodeData);
  };

  return (
    <div className="border border-line dark:border-white/[0.08] rounded-xl overflow-hidden shadow-sm bg-surface dark:bg-dark-800 mt-2">

      {/* Header */}
      <div
        onClick={onToggleAccordion}
        className="flex justify-between items-center px-3 py-2.5 cursor-pointer
                   bg-gray-50 dark:bg-white/[0.04] hover:bg-gray-100 dark:hover:bg-white/[0.07]
                   border-b border-line dark:border-white/[0.06] transition-colors"
      >
        <div className="flex items-center gap-2 font-semibold text-gray-700 dark:text-gray-200 text-sm">
          <HandMetal className="h-4 w-4 text-amber-500 dark:text-amber-400" />
          <span>{node.name}</span>
        </div>
        <div className="flex items-center gap-0.5">
          <Button
            onClick={e => { e.stopPropagation(); onRefreshIdentities(); }}
            variant="ghost" size="icon"
            className="text-content-subtle dark:text-gray-200 hover:text-amber-500 dark:hover:text-amber-400 h-7 w-7"
            title="Refresh Identities"
          >
            <RefreshCcw className="h-3.5 w-3.5" />
          </Button>
          {isOpen && (
            <Button
              onClick={e => { e.stopPropagation(); setShowModal(true); }}
              variant="ghost" size="icon"
              className="h-7 w-7 text-content-subtle dark:text-gray-300 hover:text-amber-500 dark:hover:text-amber-400"
              title="Create New Identity"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          )}
          {isOpen ? <ChevronUp className="h-4 w-4 text-content-subtle dark:text-gray-200 ml-0.5" />
                  : <ChevronDown className="h-4 w-4 text-content-subtle dark:text-gray-200 ml-0.5" />}
        </div>
      </div>

      {/* Body */}
      {isOpen && (
        <div className="p-2 space-y-1.5">

          {/* List */}
          <div className="overflow-y-auto rounded-md border border-line-subtle dark:border-white/[0.06]
                          [&::-webkit-scrollbar]:w-[3px]
                          [&::-webkit-scrollbar-thumb]:rounded-full
                          [&::-webkit-scrollbar-thumb]:bg-amber-300/40
                          dark:[&::-webkit-scrollbar-thumb]:bg-amber-600/40"
               style={{ maxHeight: 168 }}>
            {isRefreshing ? (
              (["75%", "60%", "88%"] as const).map((w, i) => (
                <div key={i} className="sidebar-skeleton-row border-b border-line-subtle dark:border-white/[0.04] last:border-0">
                  <div className="sidebar-skeleton-line" style={{ width: w }} />
                  <div className="sidebar-skeleton-dot" />
                </div>
              ))
            ) : filteredIdentities.length > 0 ? (
              filteredIdentities.map((identity, idx) => (
                <div
                  key={idx}
                  draggable
                  onDragStart={e => dragStart(e, identity)}
                  className={`group flex items-center justify-between px-2 py-[5px]
                             hover:bg-amber-50 dark:hover:bg-amber-900/10
                             cursor-grab active:cursor-grabbing transition-colors
                             ${idx < filteredIdentities.length - 1 ? "border-b border-line-subtle dark:border-white/[0.04]" : ""}`}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Mail className="flex-shrink-0 h-3 w-3 text-content-subtle dark:text-gray-500" />
                    <span className="text-micro font-medium text-content-secondary dark:text-gray-300 truncate leading-none">
                      {identity.email}
                    </span>
                  </div>
                  <button
                    onClick={() => addNode(identity)}
                    title="Add to canvas"
                    className="flex-shrink-0 ml-1.5 w-[18px] h-[18px] rounded-full flex items-center justify-center
                               opacity-80 group-hover:opacity-100
                               bg-gray-100 dark:bg-white/[0.08]
                               group-hover:bg-amber-500 dark:group-hover:bg-amber-600
                               text-content-muted dark:text-gray-400 group-hover:text-white transition-all"
                  >
                    <Plus className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-[11px] text-content-subtle dark:text-gray-600 italic text-center py-4">
                No verified identities found.
              </p>
            )}
          </div>

        </div>
      )}

      <AddIdentity open={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default ExecuteButtonNode;
